import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Shield, Heart, Users, Scale, Eye, AlertTriangle, CheckCircle2, Globe } from "lucide-react";
import { Button } from "../components/ui/button";
import SEO, { createBreadcrumbSchema } from "../components/SEO";

export default function CodigoEticaPage() {
  const { t, i18n } = useTranslation();
  const { lang } = useParams();
  const currentLang = lang || i18n.language || 'pt';
  const lastUpdate = currentLang === 'en' ? "January 23, 2026" : "23 de Janeiro de 2026";

  const paths = {
    home: '/',
    about: currentLang === 'en' ? '/en/about' : '/pt/sobre',
    privacy: currentLang === 'en' ? '/en/privacy-policy' : '/pt/politica-de-privacidade',
    terms: currentLang === 'en' ? '/en/terms-of-use' : '/pt/termos-de-uso',
    contact: currentLang === 'en' ? '/en/contact' : '/pt/contato',
  };

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  const seoConfig = currentLang === 'en' ? {
    title: "Code of Ethics - OmbudNow",
    description: "OmbudNow Code of Ethics. Our commitment to integrity, transparency, and ethical conduct in all business relationships.",
    canonical: "/en/code-of-ethics"
  } : {
    title: "Código de Ética - OmbudNow",
    description: "Código de Ética da OmbudNow. Nosso compromisso com integridade, transparência e conduta ética em todas as relações comerciais.",
    canonical: "/pt/codigo-de-etica"
  };

  const breadcrumbSchema = createBreadcrumbSchema([
    { name: currentLang === 'en' ? 'Home' : 'Início', url: '/' },
    { name: currentLang === 'en' ? 'Code of Ethics' : 'Código de Ética', url: seoConfig.canonical }
  ]);

  const content = currentLang === 'en' ? {
    badge: "Business Conduct",
    title: "Code of Ethics",
    lastUpdate: "Last updated",
    intro: {
      title: "Our Commitment",
      content: "OmbudNow is committed to the highest standards of ethics and integrity in all its operations. This Code of Ethics establishes the fundamental principles that guide our conduct and that of our employees, partners, and customers."
    },
    principles: [
      { icon: Heart, title: "Integrity", description: "We act with honesty and transparency in all our relationships, maintaining consistency between our words and actions.", color: "blue" },
      { icon: Users, title: "Respect", description: "We value diversity and treat all people with dignity, promoting an inclusive environment free from discrimination.", color: "emerald" },
      { icon: Scale, title: "Justice", description: "We ensure fair and impartial treatment for all stakeholders, basing our decisions on objective and transparent criteria.", color: "violet" },
      { icon: Eye, title: "Transparency", description: "We maintain clear and open communication, providing accurate and complete information to all interested parties.", color: "amber" }
    ],
    conduct: {
      title: "Expected Conduct",
      items: [
        "Comply with all applicable laws and regulations",
        "Protect confidential information and personal data",
        "Avoid real or apparent conflicts of interest",
        "Report irregularities through appropriate channels",
        "Treat colleagues, customers, and partners with respect",
        "Maintain confidentiality of reports received"
      ]
    },
    prohibited: {
      title: "Prohibited Conduct",
      items: [
        "Any form of corruption, bribery, or kickbacks",
        "Discrimination or harassment of any kind",
        "Manipulation of information or documents",
        "Retaliation against good-faith whistleblowers",
        "Misuse of company resources",
        "Violation of data confidentiality"
      ]
    },
    whistleblowing: {
      title: "Whistleblowing Channel",
      content: "OmbudNow provides secure and confidential channels for employees, partners, and third parties to report irregularities or unethical conduct. We guarantee:",
      items: ["Anonymity for those who wish", "Protection against retaliation", "Impartial investigation", "Feedback on progress"]
    },
    contactSection: {
      title: "Contact",
      content: "For questions about this Code of Ethics or to report a concern:"
    }
  } : {
    badge: "Conduta Empresarial",
    title: "Código de Ética",
    lastUpdate: "Última atualização",
    intro: {
      title: "Nosso Compromisso",
      content: "A OmbudNow está comprometida com os mais altos padrões de ética e integridade em todas as suas operações. Este Código de Ética estabelece os princípios fundamentais que orientam nossa conduta e a de nossos colaboradores, parceiros e clientes."
    },
    principles: [
      { icon: Heart, title: "Integridade", description: "Agimos com honestidade e transparência em todas as nossas relações, mantendo a coerência entre nossas palavras e ações.", color: "blue" },
      { icon: Users, title: "Respeito", description: "Valorizamos a diversidade e tratamos todas as pessoas com dignidade, promovendo um ambiente inclusivo e livre de discriminação.", color: "emerald" },
      { icon: Scale, title: "Justiça", description: "Garantimos tratamento justo e imparcial a todos os stakeholders, baseando nossas decisões em critérios objetivos e transparentes.", color: "violet" },
      { icon: Eye, title: "Transparência", description: "Mantemos comunicação clara e aberta, fornecendo informações precisas e completas a todas as partes interessadas.", color: "amber" }
    ],
    conduct: {
      title: "Condutas Esperadas",
      items: [
        "Cumprir todas as leis e regulamentações aplicáveis",
        "Proteger informações confidenciais e dados pessoais",
        "Evitar conflitos de interesse reais ou aparentes",
        "Reportar irregularidades através dos canais apropriados",
        "Tratar colegas, clientes e parceiros com respeito",
        "Manter a confidencialidade das denúncias recebidas"
      ]
    },
    prohibited: {
      title: "Condutas Proibidas",
      items: [
        "Qualquer forma de corrupção, suborno ou propina",
        "Discriminação ou assédio de qualquer natureza",
        "Manipulação de informações ou documentos",
        "Retaliação contra denunciantes de boa-fé",
        "Uso indevido de recursos da empresa",
        "Violação de confidencialidade de dados"
      ]
    },
    whistleblowing: {
      title: "Canal de Denúncias",
      content: "A OmbudNow disponibiliza canais seguros e confidenciais para que colaboradores, parceiros e terceiros possam reportar irregularidades ou condutas antiéticas. Garantimos:",
      items: ["Anonimato para quem desejar", "Proteção contra retaliação", "Investigação imparcial", "Feedback sobre o andamento"]
    },
    contactSection: {
      title: "Contato",
      content: "Para dúvidas sobre este Código de Ética ou para reportar uma preocupação:"
    }
  };

  const colorMap = {
    blue: { bg: "bg-blue-50", icon: "text-blue-600", border: "border-blue-100" },
    emerald: { bg: "bg-emerald-50", icon: "text-emerald-600", border: "border-emerald-100" },
    violet: { bg: "bg-violet-50", icon: "text-violet-600", border: "border-violet-100" },
    amber: { bg: "bg-amber-50", icon: "text-amber-600", border: "border-amber-100" }
  };

  return (
    <div className="min-h-screen bg-white font-ibm" data-testid="ethics-page">
      <SEO
        title={seoConfig.title}
        description={seoConfig.description}
        canonical={seoConfig.canonical}
        lang={currentLang}
        schemas={[breadcrumbSchema]}
      />

      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to={paths.home} className="flex items-center gap-3" data-testid="header-logo">
              <div className="w-9 h-9 bg-slate-900 rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg font-bold text-slate-900 font-chivo tracking-tight">OMBUDNOW</span>
            </Link>

            <nav className="hidden md:flex items-center gap-8">
              <Link to={paths.about} className="text-slate-600 hover:text-slate-900 transition-colors text-sm font-medium">
                {currentLang === 'en' ? 'About' : 'Sobre'}
              </Link>
              <Link to={currentLang === 'en' ? '/en/blog' : '/pt/blog'} className="text-slate-600 hover:text-slate-900 transition-colors text-sm font-medium">
                Blog
              </Link>
              <Link to={paths.contact} className="text-slate-600 hover:text-slate-900 transition-colors text-sm font-medium">
                {currentLang === 'en' ? 'Contact' : 'Contato'}
              </Link>
              <div className="flex items-center gap-1.5 text-slate-500 text-sm border-l border-slate-200 pl-6 ml-2">
                <Globe className="w-4 h-4" />
                <button
                  onClick={() => changeLanguage('pt')}
                  className={`px-1 ${currentLang === 'pt' ? 'text-slate-900 font-semibold' : 'text-slate-500 hover:text-slate-700'} transition-colors`}
                >
                  PT
                </button>
                <span className="text-slate-300">/</span>
                <button
                  onClick={() => changeLanguage('en')}
                  className={`px-1 ${currentLang === 'en' ? 'text-slate-900 font-semibold' : 'text-slate-500 hover:text-slate-700'} transition-colors`}
                >
                  EN
                </button>
              </div>
            </nav>

            <Button
              className="bg-slate-900 text-white hover:bg-slate-800 font-medium px-5"
              onClick={() => window.location.href = '/onboarding?plan=free'}
            >
              {currentLang === 'en' ? 'Start free' : 'Começar grátis'}
            </Button>
          </div>
        </div>
      </header>

      <main className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Hero */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-700 px-4 py-2 rounded-full text-sm font-medium mb-4 border border-emerald-100" data-testid="ethics-badge">
              <Shield className="w-4 h-4" />
              {content.badge}
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4 font-chivo tracking-tight" data-testid="ethics-title">
              {content.title}
            </h1>
            <p className="text-slate-500">
              {content.lastUpdate}: {lastUpdate}
            </p>
          </div>

          {/* Introduction */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 mb-8" data-testid="ethics-intro">
            <h2 className="text-xl font-bold text-slate-900 mb-4 font-chivo">{content.intro.title}</h2>
            <p className="text-slate-600 leading-relaxed">{content.intro.content}</p>
          </div>

          {/* Principles Grid */}
          <div className="mb-8">
            <h2 className="text-xl font-bold text-slate-900 mb-6 font-chivo">
              {currentLang === 'en' ? 'Fundamental Principles' : 'Princípios Fundamentais'}
            </h2>
            <div className="grid md:grid-cols-2 gap-4" data-testid="ethics-principles">
              {content.principles.map((item, index) => {
                const IconComponent = item.icon;
                const colors = colorMap[item.color];
                return (
                  <div key={index} className={`rounded-xl border ${colors.border} ${colors.bg} p-6`}>
                    <div className={`w-10 h-10 ${colors.bg} rounded-lg flex items-center justify-center mb-3 border ${colors.border}`}>
                      <IconComponent className={`w-5 h-5 ${colors.icon}`} />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 mb-2 font-chivo">{item.title}</h3>
                    <p className="text-slate-600 text-sm leading-relaxed">{item.description}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Expected Conduct */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 mb-8" data-testid="ethics-conduct">
            <h2 className="text-xl font-bold text-slate-900 mb-6 font-chivo">{content.conduct.title}</h2>
            <ul className="space-y-3">
              {content.conduct.items.map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-600">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Prohibited Conduct */}
          <div className="bg-white rounded-xl border border-red-200 shadow-sm p-8 mb-8" data-testid="ethics-prohibited">
            <h2 className="text-xl font-bold text-slate-900 mb-6 font-chivo">{content.prohibited.title}</h2>
            <ul className="space-y-3">
              {content.prohibited.items.map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-600">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Whistleblowing */}
          <div className="bg-slate-50 rounded-xl border border-slate-200 p-8 mb-8" data-testid="ethics-whistleblowing">
            <h2 className="text-xl font-bold text-slate-900 mb-4 font-chivo">{content.whistleblowing.title}</h2>
            <p className="text-slate-600 leading-relaxed mb-6">{content.whistleblowing.content}</p>
            <div className="grid md:grid-cols-2 gap-3">
              {content.whistleblowing.items.map((item, index) => (
                <div key={index} className="flex items-center gap-3 bg-white rounded-lg p-3 border border-slate-200">
                  <CheckCircle2 className="w-5 h-5 text-blue-600" />
                  <span className="text-slate-700 text-sm font-medium">{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Contact */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8" data-testid="ethics-contact">
            <h2 className="text-xl font-bold text-slate-900 mb-4 font-chivo">{content.contactSection.title}</h2>
            <p className="text-slate-600 leading-relaxed mb-4">{content.contactSection.content}</p>
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
              <p className="text-slate-600">
                <strong className="text-slate-900">Email:</strong>{" "}
                <a href="mailto:support@ombudnow.com" className="text-blue-600 hover:underline">
                  support@ombudnow.com
                </a>
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#0B0F19] py-12 mt-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-white/10 rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg font-bold text-white font-chivo">OMBUDNOW</span>
            </div>
            <p className="text-slate-500 text-sm">
              &copy; 2025 OmbudNow. {currentLang === 'en' ? 'All rights reserved.' : 'Todos os direitos reservados.'}
            </p>
            <div className="flex items-center gap-6">
              <Link to={paths.privacy} className="text-slate-500 hover:text-white text-sm transition-colors">
                {currentLang === 'en' ? 'Privacy' : 'Privacidade'}
              </Link>
              <Link to={paths.terms} className="text-slate-500 hover:text-white text-sm transition-colors">
                {currentLang === 'en' ? 'Terms' : 'Termos'}
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
